import React from 'react';
import { Link } from 'react-router-dom';

/**
 * TrialBanner - tells an admin their trial has lapsed, or is about to.
 *
 * WHY THIS EXISTS
 * ---------------
 * /api/admin/validate used to return only subscription_status, so the client
 * could tell "suspended" from "not suspended" and nothing else. A tenant whose
 * trial had lapsed but whom the nightly cron had not yet suspended came back as
 * not suspended, so the normal dashboard rendered, and every write the admin
 * attempted failed with a 403 from the trial gate in authenticateAdmin. The UI
 * said everything was fine and the server disagreed on every click.
 *
 * The validate route now returns the tier, the trial end date and the days
 * remaining, which is what this banner reads.
 *
 * Props are the shape of the `tenant` object from /api/admin/validate.
 */
function TrialBanner({ tenant }) {
  if (!tenant) return null;

  const { isTrialExpired, trialEndsAt, trialDaysRemaining, subscriptionTier } = tenant;

  if (subscriptionTier !== 'free_trial') return null;

  const formatted = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    })
    : null;

  if (isTrialExpired) {
    return (
      <div className="trial-banner trial-banner-expired" role="status">
        <span className="trial-banner-text">
          <span className="trial-banner-icon">⚠</span>
          {formatted
            ? `Your trial ended on ${formatted}. Upgrade to keep making changes.`
            : 'Your trial has ended. Upgrade to keep making changes.'}
        </span>
        <Link to="/admin/billing" className="trial-banner-cta">
          Upgrade
        </Link>
      </div>
    );
  }

  // Warn over the last week, matching the 7 day threshold the cron sends its
  // first warning email on, so the banner and the email agree.
  const days = Number(trialDaysRemaining);
  if (!Number.isFinite(days) || days > 7 || days <= 0) return null;

  return (
    <div className="trial-banner trial-banner-warning" role="status">
      <span className="trial-banner-text">
        <span className="trial-banner-icon">⏳</span>
        {days === 1
          ? 'Your trial ends tomorrow. Upgrade to keep making changes.'
          : `Your trial ends in ${days} days. Upgrade to keep making changes.`}
      </span>
      <Link to="/admin/billing" className="trial-banner-cta">
        Upgrade
      </Link>
    </div>
  );
}

export default TrialBanner;
